import { Suspense, useEffect, useMemo, useRef } from 'react'
import { ErrorBoundary } from '../common/ErrorBoundary'
import { useThemeOverride, useResetPageTitle } from '../common/appEffects'
import LoadingPlaceholder from '../common/LoadingPlaceholder'
import { ffUseNav3 } from './navUtils'
import { setValuesInternal } from '../../framework/actions'
import type { VFC, ComponentType } from 'react'
import type { NavItem, ComponentWrapperProps } from '../../framework/microAppTypes'

const eqKeys = {
  routeContext: 1,
  main: 1,
  auth: 1,
  match: 1,
  params: 1,
} as Record<string, 1>
const shallowEqProps = (current: any, next: any) => {
  if (!current) {
    return false
  }
  const aKeys = Object.keys(current)
  const bKeys = Object.keys(next)
  if (aKeys.length !== bKeys.length) {
    return false
  }
  for (const key of aKeys) {
    const eq = eqKeys[key] ? shallowEqProps : Object.is
    if (!eq(current[key], next[key])) {
      // console.info('diff', key, current[key], next[key])
      return false
    }
  }
  return true
}

export const useMemoProps = (value: any): any => {
  const ref = useRef()
  if (!shallowEqProps(ref.current, value)) {
    ref.current = value
  }
  return ref.current
}

export const useGetThemeOverride = (navItem: NavItem) => useMemo(() => {
  if (navItem._theme_override) {
    return navItem._theme_override
  }
  if (navItem.contentClassName) {
    return navItem.contentClassName.includes('sparky-dark-only') ? 'dark' :
      navItem.contentClassName.includes('sparky-light-only') ? 'light' : undefined
  }
  return undefined
}, [navItem._theme_override, navItem.contentClassName])

export const useAutoCollapse = (navItem: NavItem) => {
  useEffect(() => {
    if (navItem.autoCollapse && ffUseNav3()) { // nav v2 handle it in top left nav
      setValuesInternal({ isSideNavCollapsed: true })
    }
  }, [navItem.autoCollapse])
}

export const MemoComponentWrapper: VFC<ComponentWrapperProps> = ({ navItem, renderProps, parentProps }) => {
  const stableRenderProps = useMemoProps(renderProps)
  const routeProps = useMemoProps(useMemo(() => {
    const { state, appBar, sideMenuDrawer, ...routeProps } = parentProps // ignore those always changing
    Object.assign(routeProps, navItem.mapStateToProps?.(state) ?? {
      main: state.main,
      auth: state.auth,
      theme: state.theme,
    }, {
      navItem: navItem,
    }, navItem.appVars && { vars: navItem.appVars })
    return routeProps
  }, [navItem, parentProps]))
  const NavComp: ComponentType = navItem.component as ComponentType
  const loadingFallback = useMemo(() => <LoadingPlaceholder />, [])
  const errorContext = navItem.microAppId || navItem.appName
  useThemeOverride(useGetThemeOverride(navItem))
  useAutoCollapse(navItem)
  useResetPageTitle()
  return useMemo(() => {
    return (
      <ErrorBoundary context={errorContext}>
        <Suspense fallback={loadingFallback}>
          <NavComp {...stableRenderProps} {...routeProps} />
        </Suspense>
      </ErrorBoundary>
    )
  }, [NavComp, errorContext, loadingFallback, routeProps, stableRenderProps])
}

export const ComponentWrapper: VFC<ComponentWrapperProps> = ({ navItem, renderProps, parentProps }) => {
  const routeProps = useMemo(() => {
    const { state, appBar, sideMenuDrawer, ...routeProps } = parentProps // ignore those always changing
    Object.assign(routeProps, navItem.mapStateToProps?.(state) ?? {
      main: state.main,
      auth: state.auth,
      theme: state.theme,
    }, {
      navItem: navItem,
      vars: navItem.appVars,
    })
    return routeProps
  }, [navItem, parentProps])
  const NavComp: ComponentType = navItem.component as ComponentType
  const errorContext = navItem.microAppId || navItem.appName
  useThemeOverride(useGetThemeOverride(navItem))
  useAutoCollapse(navItem)
  useResetPageTitle()
  return (
    <ErrorBoundary context={errorContext}>
      <Suspense fallback={<LoadingPlaceholder />}>
        <NavComp {...renderProps} {...routeProps} />
      </Suspense>
    </ErrorBoundary>
  )
}
